import React from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Control, Controller, FieldErrors, UseFormSetValue, useWatch, UseFormResetField } from 'react-hook-form'
import { InputField } from '@/components/molecules/input-field'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'
import { SalesSubjectTaxRoundingType, SalesSubjectTaxRoundingUnit } from '@/models/sales-manager/sales'
import { FormValue } from './subject-tax-detail-modal'

type Props = {
  control: Control<FormValue, any>
  errors: FieldErrors<FormValue>
  setValue: UseFormSetValue<FormValue>
  resetField: UseFormResetField<FormValue>
}

export const SubjectTaxRateField: React.FC<Props> = ({ control, errors, setValue, resetField }) => {
  const { t } = useTranslation()
  const isLimitEnabled = useWatch({ control, name: 'roomChargeLimitEnabled' })

  return (
    <div css={containerStyle}>
      {/* 宿泊料金の N % 入力 */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div css={fieldLabelStyle}>{t('saleSubjectTax.RoomChargeOf')}</div>
          <Controller
            control={control}
            rules={{
              required: t('Required field has not been entered'),
              pattern: { value: /^\d{1,2}(\.\d)?$/, message: t('Please enter numbers only, without symbols or spaces') },
            }}
            name="roomChargeRate"
            render={({ field: { onChange, value } }) => (
              <InputField
                type="text"
                value={value}
                handleChangeData={onChange}
                placeholder="10"
                marginBottom={0}
                width="65px"
                fieldWidth="unset"
                error={errors?.roomChargeRate ? ' ' : ''}
              />
            )}
          />
          <div css={fieldLabelStyle}>%</div>
        </div>
        {errors?.roomChargeRate && <div css={errorMessageStyle}>{errors.roomChargeRate.message}</div>}
      </div>

      <div css={dividerStyle} />

      {/* 端数処理 */}
      <div css={inputContainerStyle}>
        <div css={fieldLabelStyle}>{t('saleSubjectTax.Rounding')}</div>
        <div css={roundingRowStyle}>
          <div css={subLabelStyle}>{t('saleSubjectTax.RoundingUnit')}</div>
          <Controller
            control={control}
            rules={{ required: t('Required field has not been entered') }}
            name="roomChargeRoundingUnit"
            render={({ field: { onChange, value } }) => (
              <RadioGroupField
                // HTML の input[value] は常に文字列になるため、数値型の Enum を文字列に変換して渡す
                onChange={e => onChange(Number(e.currentTarget.value) as SalesSubjectTaxRoundingUnit)}
                value={value !== undefined ? String(value) : undefined}
                items={[
                  { value: String(SalesSubjectTaxRoundingUnit.Hundred), label: t('saleSubjectTax.Hundred') },
                  { value: String(SalesSubjectTaxRoundingUnit.Thousand), label: t('saleSubjectTax.Thousand') },
                ]}
                groupCss={css({ gap: 24 })}
                style={{ radioSize: 18, labelMargin: 5 }}
              />
            )}
          />
        </div>
        <div css={roundingRowStyle}>
          <div css={subLabelStyle}>{t('saleSubjectTax.Rounding')}</div>
          <Controller
            control={control}
            rules={{ required: t('Required field has not been entered') }}
            name="roomChargeRoundingType"
            render={({ field: { onChange, value } }) => (
              <RadioGroupField
                onChange={onChange}
                value={value}
                items={[
                  { value: SalesSubjectTaxRoundingType.RoundDown, label: t('saleSubjectTax.RoundDown') },
                  { value: SalesSubjectTaxRoundingType.RoundUp, label: t('saleSubjectTax.RoundUp') },
                ]}
                groupCss={css({ gap: 24 })}
                style={{ radioSize: 18, labelMargin: 5 }}
              />
            )}
          />
        </div>
      </div>

      <div css={dividerStyle} />

      {/* 宿泊料金の上限・下限設定 */}
      <div css={inputContainerStyle}>
        <div css={upperLimitHeaderStyle}>
          <div css={fieldLabelStyle}>{t('saleSubjectTax.UpperAndLowerLimitSetting')}</div>
          <Controller
            control={control}
            name="roomChargeLimitEnabled"
            render={({ field: { value, onChange } }) => (
              <ToggleButton
                value={value}
                onChange={() => {
                  // トグルをOFFにするとき、上限・下限金額の入力値をクリアする
                  // トグルONにするとき、上限・下限金額をデフォルト値に戻す
                  if (value) {
                    setValue('roomChargeUpperLimit', null)
                    setValue('roomChargeLowerLimit', null)
                  } else {
                    resetField('roomChargeUpperLimit')
                    resetField('roomChargeLowerLimit')
                  }
                  onChange(!value)
                }}
                label={{ on: t('enable'), off: t('disable') }}
              />
            )}
          />
        </div>

        {/* トグルON時のみ上限・下限金額フィールドを表示。Controllerのアンマウントにより、RHFのバリデーションも自動解除される */}
        {isLimitEnabled && (
          <div css={upperLimitAmountStyle}>
            <div css={upperLimitAmountRowStyle}>
              <div css={subLabelStyle}>{t('saleSubjectTax.UpperLimitAmount')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Controller
                  control={control}
                  rules={{
                    pattern: { value: /^\d+$/, message: t('Please enter numbers only, without symbols or spaces') },
                    min: { value: 1, message: t('saleSubjectTax.MinimumOne') },
                  }}
                  name="roomChargeUpperLimit"
                  render={({ field: { onChange, value } }) => (
                    <InputField
                      type="text"
                      value={value ?? ''}
                      handleChangeData={onChange}
                      placeholder="10000"
                      marginBottom={0}
                      width="120px"
                      fieldWidth="unset"
                      error={errors?.roomChargeUpperLimit ? ' ' : ''}
                    />
                  )}
                />
                <div css={fieldLabelStyle}>円</div>
              </div>
            </div>
            {errors?.roomChargeUpperLimit && <div css={errorMessageStyle}>{errors.roomChargeUpperLimit.message}</div>}
            <div css={upperLimitAmountRowStyle}>
              <div css={subLabelStyle}>{t('saleSubjectTax.LowerLimitAmount')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Controller
                  control={control}
                  rules={{
                    pattern: { value: /^\d+$/, message: t('Please enter numbers only, without symbols or spaces') },
                    min: { value: 1, message: t('saleSubjectTax.MinimumOne') },
                  }}
                  name="roomChargeLowerLimit"
                  render={({ field: { onChange, value } }) => (
                    <InputField
                      type="text"
                      value={value ?? ''}
                      handleChangeData={onChange}
                      placeholder="10000"
                      marginBottom={0}
                      width="120px"
                      fieldWidth="unset"
                      error={errors?.roomChargeLowerLimit ? ' ' : ''}
                    />
                  )}
                />
                <div css={fieldLabelStyle}>円</div>
              </div>
            </div>
            {errors?.roomChargeLowerLimit && <div css={errorMessageStyle}>{errors.roomChargeLowerLimit.message}</div>}
          </div>
        )}
      </div>
    </div>
  )
}

const containerStyle = css({
  border: '1px solid #CCCCCC',
  borderRadius: 10,
  padding: '12px 24px',
})

const fieldLabelStyle = css({
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: 1.2,
  color: '#676767',
  paddingLeft: 8,
  paddingRight: 8,
})

const subLabelStyle = css({
  fontSize: 12,
  color: '#676767',
  paddingLeft: 8,
})

const inputContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
})

const roundingRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
})

const upperLimitHeaderStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const upperLimitAmountStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

const upperLimitAmountRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
})

const dividerStyle = css({
  borderBottom: '1px solid #CCCCCC',
  margin: '12px 0',
})

const errorMessageStyle = css({
  fontSize: 10,
  color: 'rgba(255,0,0,0.5)',
  marginTop: 4,
})
